/**
 * Свободный текст в параметрах заклинаний — по-русски.
 *
 * Параметры заклинания состоят из двух разных вещей. Первая —
 * устойчивые обороты: «Action», «Concentration, up to 1 minute»,
 * «V, S, M». Они разбираются по грамматике и переводятся словарём
 * интерфейса (`spell-facts.ts`). Вторая — свободный текст, который
 * словарём не разберёшь: материальный компонент в скобках («a strip
 * of white cloth») и условие реакции («which you take when you are
 * hit by an attack roll»). Он переводится как описание — целыми
 * оборотами, и лежит здесь.
 *
 * Ключ — слаг английского оборота, а не идентификатор записи:
 * один и тот же компонент стоит у нескольких заклинаний, и перевод
 * у него один. Английский оригинал лежит рядом с переводом и
 * не выдумывается: тест сверяет каждый с самим контентом
 * (`spell-phrases.test.ts`), поэтому «забыли перевести» здесь —
 * это красный тест, а не английская строка посреди русской карточки.
 *
 * Числа не трогаются: «worth 100+ GP» — «стоимостью от 100 золотых».
 * Это тоже проверяется тестом: перепутанная цена приезжает к игроку
 * числом на экране, и заметить её глазами в двух сотнях строк нельзя.
 */

export type SpellPhrase = {
  readonly ru: string
  readonly en: string
}

/** Материальные компоненты: текст в скобках после «M». */
export const SPELL_MATERIALS: Readonly<Record<string, SpellPhrase>> = {
  '1_copper_piece': { ru: '1 медная монета', en: '1 Copper Piece' },
  '2_copper_pieces_which_the_spell_consumes': {
    ru: '2 медные монеты, расходуются заклинанием',
    en: '2 Copper Pieces, which the spell consumes',
  },
  a_ball_of_bat_guano_and_sulfur: {
    ru: 'шарик из гуано летучей мыши и серы',
    en: 'a ball of bat guano and sulfur',
  },
  a_ball_of_wax: { ru: 'восковой шарик', en: 'a ball of wax' },
  a_bell_and_silver_wire: {
    ru: 'колокольчик и серебряная проволока',
    en: 'a bell and silver wire',
  },
  a_bit_of_bat_fur: { ru: 'кусочек шерсти летучей мыши', en: 'a bit of bat fur' },
  a_bit_of_fleece: { ru: 'клочок шерсти', en: 'a bit of fleece' },
  a_bit_of_fur_and_a_crystal_rod: {
    ru: 'кусочек меха и хрустальный стержень',
    en: 'a bit of fur and a crystal rod',
  },
  a_bit_of_gauze: { ru: 'кусочек марли', en: 'a bit of gauze' },
  a_bit_of_phosphorus: { ru: 'немного фосфора', en: 'a bit of phosphorus' },
  a_bit_of_phosphorus_or_a_firefly: {
    ru: 'немного фосфора или светлячок',
    en: 'a bit of phosphorus or a firefly',
  },
  a_bit_of_pork_rind_or_butter: {
    ru: 'кусочек свиной шкурки или масла',
    en: 'a bit of pork rind or butter',
  },
  a_bit_of_spiderweb: { ru: 'кусочек паутины', en: 'a bit of spiderweb' },
  a_bit_of_string_and_of_wood: {
    ru: 'кусочек верёвки и дерева',
    en: 'a bit of string and of wood',
  },
  a_candle: { ru: 'свеча', en: 'a candle' },
  a_caterpillar_cocoon: { ru: 'кокон гусеницы', en: 'a caterpillar cocoon' },
  a_chest_3_feet_by_2_feet_by_2_feet_constructed_from_rare_materia: {
    ru: 'сундук 3 фута на 2 фута на 2 фута из редких материалов стоимостью от 5000 золотых и крошечная копия этого сундука из тех же материалов стоимостью от 50 золотых',
    en: 'a chest, 3 feet by 2 feet by 2 feet, constructed from rare materials worth 5,000+ GP, and a Tiny replica of the chest made from the same materials worth 50+ GP',
  },
  a_chip_of_mica: { ru: 'кусочек слюды', en: 'a chip of mica' },
  a_cockatrice_feather: { ru: 'перо кокатрикса', en: 'a cockatrice feather' },
  a_copper_wire: { ru: 'медная проволока', en: 'a copper wire' },
  a_crystal_bead: { ru: 'хрустальная бусина', en: 'a crystal bead' },
  a_cube_of_granite: { ru: 'гранитный кубик', en: 'a cube of granite' },
  a_diamond_worth_1_000_gp_which_the_spell_consumes: {
    ru: 'алмаз стоимостью от 1000 золотых, расходуется заклинанием',
    en: 'a diamond worth 1,000+ GP, which the spell consumes',
  },
  a_diamond_worth_1_000_gp_which_the_spell_consumes_and_a_sealable: {
    ru: 'алмаз стоимостью от 1000 золотых, расходуется заклинанием, и закрывающийся сосуд стоимостью от 2000 золотых, вмещающий клонируемое существо',
    en: 'a diamond worth 1,000+ GP, which the spell consumes, and a sealable vessel worth 2,000+ GP that is large enough to hold the creature being cloned',
  },
  a_diamond_worth_300_gp_which_the_spell_consumes: {
    ru: 'алмаз стоимостью от 300 золотых, расходуется заклинанием',
    en: 'a diamond worth 300+ GP, which the spell consumes',
  },
  a_diamond_worth_500_gp_which_the_spell_consumes: {
    ru: 'алмаз стоимостью от 500 золотых, расходуется заклинанием',
    en: 'a diamond worth 500+ GP, which the spell consumes',
  },
  a_diamond_worth_50_gp: {
    ru: 'алмаз стоимостью от 50 золотых',
    en: 'a diamond worth 50+ GP',
  },
  a_diamond_worth_5_000_gp: {
    ru: 'алмаз стоимостью от 5000 золотых',
    en: 'a diamond worth 5,000+ GP',
  },
  a_dried_carrot: { ru: 'сушёная морковь', en: 'a dried carrot' },
  a_drop_of_alcohol: { ru: 'капля спирта', en: 'a drop of alcohol' },
  a_drop_of_bile: { ru: 'капля жёлчи', en: 'a drop of bile' },
  a_drop_of_bitumen_and_a_spider: {
    ru: 'капля битума и паук',
    en: 'a drop of bitumen and a spider',
  },
  a_drop_of_blood: { ru: 'капля крови', en: 'a drop of blood' },
  a_drop_of_blood_a_piece_of_flesh_and_a_pinch_of_bone_dust: {
    ru: 'капля крови, кусок плоти и щепотка костной пыли',
    en: 'a drop of blood, a piece of flesh, and a pinch of bone dust',
  },
  a_drop_of_honey: { ru: 'капля мёда', en: 'a drop of honey' },
  a_drop_of_mercury: { ru: 'капля ртути', en: 'a drop of mercury' },
  a_drop_of_mercury_a_dollop_of_gum_arabic_and_a_wisp_of_smoke: {
    ru: 'капля ртути, комок гуммиарабика и струйка дыма',
    en: 'a drop of mercury, a dollop of gum arabic, and a wisp of smoke',
  },
  a_drop_of_molasses: { ru: 'капля патоки', en: 'a drop of molasses' },
  a_drop_of_water_or_a_piece_of_ice: {
    ru: 'капля воды или кусочек льда',
    en: 'a drop of water or a piece of ice',
  },
  a_fan_and_a_feather: { ru: 'веер и перо', en: 'a fan and a feather' },
  a_feather: { ru: 'перо', en: 'a feather' },
  a_firefly_or_phosphorescent_moss: {
    ru: 'светлячок или светящийся мох',
    en: 'a firefly or phosphorescent moss',
  },
  a_flask_of_holy_water_worth_25_gp_which_the_spell_consumes: {
    ru: 'склянка святой воды стоимостью от 25 золотых, расходуется заклинанием',
    en: 'a flask of Holy Water worth 25+ GP, which the spell consumes',
  },
  a_focus_worth_100_gp_either_a_jeweled_horn_for_hearing_or_a_glas: {
    ru: 'фокусировка стоимостью от 100 золотых: украшенный самоцветами рог для слуха или стеклянный глаз для зрения',
    en: 'a focus worth 100+ GP, either a jeweled horn for hearing or a glass eye for seeing',
  },
  a_focus_worth_1_000_gp_such_as_a_crystal_ball_mirror_or_water_fi: {
    ru: 'фокусировка стоимостью от 1000 золотых — хрустальный шар, зеркало или чаша с водой',
    en: 'a focus worth 1,000+ GP, such as a crystal ball, mirror, or water-filled font',
  },
  a_forked_metal_rod_worth_250_gp_and_attuned_to_a_plane_of_existe: {
    ru: 'раздвоенный металлический стержень стоимостью от 250 золотых, настроенный на план существования',
    en: 'a forked, metal rod worth 250+ GP and attuned to a plane of existence',
  },
  a_forked_twig: { ru: 'раздвоенный прутик', en: 'a forked twig' },
  a_fractured_rock: { ru: 'расколотый камень', en: 'a fractured rock' },
  a_gem_crystal_or_reliquary_worth_500_gp: {
    ru: 'самоцвет, кристалл или реликварий стоимостью от 500 золотых',
    en: 'a gem, crystal, or reliquary worth 500+ GP',
  },
  a_gem_encrusted_bowl_worth_1_000_gp_which_the_spell_consumes: {
    ru: 'чаша, украшенная самоцветами, стоимостью от 1000 золотых, расходуется заклинанием',
    en: 'a gem-encrusted bowl worth 1,000+ GP, which the spell consumes',
  },
  a_gem_encrusted_statuette_of_yourself_worth_1_500_gp: {
    ru: 'ваша статуэтка, украшенная самоцветами, стоимостью от 1500 золотых',
    en: 'a gem-encrusted statuette of yourself worth 1,500+ GP',
  },
  a_glass_bead: { ru: 'стеклянная бусина', en: 'a glass bead' },
  a_glass_sphere: { ru: 'стеклянный шар', en: 'a glass sphere' },
  a_grasshopper_s_hind_leg: {
    ru: 'задняя нога кузнечика',
    en: 'a grasshopper’s hind leg',
  },
  a_handful_of_bark: { ru: 'горсть коры', en: 'a handful of bark' },
  a_handful_of_sand: { ru: 'горсть песка', en: 'a handful of sand' },
  a_handful_of_thorns: { ru: 'горсть шипов', en: 'a handful of thorns' },
  a_holy_symbol_worth_5_gp: {
    ru: 'священный символ стоимостью от 5 золотых',
    en: 'a Holy Symbol worth 5+ GP',
  },
  a_hot_pepper: { ru: 'острый перец', en: 'a hot pepper' },
  a_hummingbird_feather: { ru: 'перо колибри', en: 'a hummingbird feather' },
  a_jade_circlet_worth_1_500_gp: {
    ru: 'нефритовый обруч стоимостью от 1500 золотых',
    en: 'a jade circlet worth 1,500+ GP',
  },
  a_jewel_worth_1_000_gp_which_the_spell_consumes: {
    ru: 'драгоценность стоимостью от 1000 золотых, расходуется заклинанием',
    en: 'a jewel worth 1,000+ GP, which the spell consumes',
  },
  a_key_ring_with_no_keys: {
    ru: 'кольцо для ключей без ключей',
    en: 'a key ring with no keys',
  },
  a_leather_strap: { ru: 'кожаный ремешок', en: 'a leather strap' },
  a_legume_seed: { ru: 'семя бобового растения', en: 'a legume seed' },
  a_locust: { ru: 'саранча', en: 'a locust' },
  a_lodestone_and_dust: { ru: 'магнетит и пыль', en: 'a lodestone and dust' },
  a_lodestone_and_iron_filings: {
    ru: 'магнетит и железные опилки',
    en: 'a lodestone and iron filings',
  },
  a_magnifying_glass: { ru: 'увеличительное стекло', en: 'a magnifying glass' },
  a_metal_spring: { ru: 'металлическая пружина', en: 'a metal spring' },
  a_miniature_crystal_sphere: {
    ru: 'миниатюрный хрустальный шар',
    en: 'a miniature crystal sphere',
  },
  a_miniature_door_worth_15_gp: {
    ru: 'миниатюрная дверь стоимостью от 15 золотых',
    en: 'a miniature door worth 15+ GP',
  },
  a_miniature_shovel: { ru: 'миниатюрная лопата', en: 'a miniature shovel' },
  a_miniature_sword_worth_250_gp: {
    ru: 'миниатюрный меч стоимостью от 250 золотых',
    en: 'a miniature sword worth 250+ GP',
  },
  a_miniature_umbrella: { ru: 'миниатюрный зонт', en: 'a miniature umbrella' },
  a_miniature_ziggurat: { ru: 'миниатюрный зиккурат', en: 'a miniature ziggurat' },
  a_mitten: { ru: 'варежка', en: 'a mitten' },
  a_mix_of_vinegar_and_honey: {
    ru: 'смесь уксуса и мёда',
    en: 'a mix of vinegar and honey',
  },
  a_mix_of_water_and_sand: { ru: 'смесь воды и песка', en: 'a mix of water and sand' },
  a_mixture_of_water_and_dust: {
    ru: 'смесь воды и пыли',
    en: 'a mixture of water and dust',
  },
  a_moonseed_leaf: { ru: 'лист луносемянника', en: 'a moonseed leaf' },
  a_morsel_of_food: { ru: 'кусочек пищи', en: 'a morsel of food' },
  a_mushroom: { ru: 'гриб', en: 'a mushroom' },
  a_paintbrush: { ru: 'кисть', en: 'a paintbrush' },
  a_pair_of_platinum_rings_worth_50_gp_each_which_you_and_the_targ: {
    ru: 'пара платиновых колец стоимостью от 50 золотых каждое: вы и цель носите их всю длительность',
    en: 'a pair of platinum rings worth 50+ GP each, which you and the target must wear for the duration',
  },
  a_pearl_worth_100_gp: {
    ru: 'жемчужина стоимостью от 100 золотых',
    en: 'a pearl worth 100+ GP',
  },
  a_pentacle: { ru: 'пентакль', en: 'a pentacle' },
  a_piece_of_charcoal: { ru: 'кусок угля', en: 'a piece of charcoal' },
  a_piece_of_cork: { ru: 'кусочек пробки', en: 'a piece of cork' },
  a_piece_of_cured_leather: {
    ru: 'кусок выделанной кожи',
    en: 'a piece of cured leather',
  },
  a_piece_of_iron_and_a_flame: {
    ru: 'кусок железа и огонь',
    en: 'a piece of iron and a flame',
  },
  a_piece_of_quartz: { ru: 'кусок кварца', en: 'a piece of quartz' },
  a_piece_of_sunstone: { ru: 'кусок солнечного камня', en: 'a piece of sunstone' },
  a_pinch_of_colorful_sand: {
    ru: 'щепотка цветного песка',
    en: 'a pinch of colorful sand',
  },
  a_pinch_of_confetti: { ru: 'щепотка конфетти', en: 'a pinch of confetti' },
  a_pinch_of_diamond_dust_worth_25_gp_which_the_spell_consumes: {
    ru: 'щепотка алмазной пыли стоимостью от 25 золотых, расходуется заклинанием',
    en: 'a pinch of diamond dust worth 25+ GP, which the spell consumes',
  },
  a_pinch_of_dirt: { ru: 'щепотка земли', en: 'a pinch of dirt' },
  a_pinch_of_powdered_iron: {
    ru: 'щепотка железного порошка',
    en: 'a pinch of powdered iron',
  },
  a_pinch_of_sand_or_rose_petals: {
    ru: 'щепотка песка или лепестки роз',
    en: 'a pinch of sand or rose petals',
  },
  a_pinch_of_sesame_seeds: {
    ru: 'щепотка семян кунжута',
    en: 'a pinch of sesame seeds',
  },
  a_pinch_of_soot_and_salt: {
    ru: 'щепотка сажи и соли',
    en: 'a pinch of soot and salt',
  },
  a_pinch_of_sulfur: { ru: 'щепотка серы', en: 'a pinch of sulfur' },
  a_pinch_of_talc: { ru: 'щепотка талька', en: 'a pinch of talc' },
  a_prayer_scroll: { ru: 'молитвенный свиток', en: 'a prayer scroll' },
  a_prayer_wheel: { ru: 'молитвенное колесо', en: 'a prayer wheel' },
  a_reliquary_worth_1_000_gp: {
    ru: 'реликварий стоимостью от 1000 золотых',
    en: 'a reliquary worth 1,000+ GP',
  },
  a_rotten_egg: { ru: 'тухлое яйцо', en: 'a rotten egg' },
  a_sapphire_worth_1_000_gp: {
    ru: 'сапфир стоимостью от 1000 золотых',
    en: 'a sapphire worth 1,000+ GP',
  },
  a_segment_of_rope: { ru: 'отрезок верёвки', en: 'a segment of rope' },
  a_set_of_divination_tools_such_as_cards_or_runes_worth_100_gp: {
    ru: 'набор принадлежностей для гадания — карты или руны — стоимостью от 100 золотых',
    en: 'a set of divination tools—such as cards or runes—worth 100+ GP',
  },
  a_shard_of_glass: { ru: 'осколок стекла', en: 'a shard of glass' },
  a_shard_of_glass_from_a_mirror: {
    ru: 'осколок зеркального стекла',
    en: 'a shard of glass from a mirror',
  },
  a_shaving_of_licorice_root: {
    ru: 'стружка корня солодки',
    en: 'a shaving of licorice root',
  },
  a_short_reed: { ru: 'короткая тростинка', en: 'a short reed' },
  a_silver_rod_worth_10_gp: {
    ru: 'серебряный стержень стоимостью от 10 золотых',
    en: 'a silver rod worth 10+ GP',
  },
  a_silver_whistle: { ru: 'серебряный свисток', en: 'a silver whistle' },
  a_small_crystal_or_glass_cone: {
    ru: 'маленький хрустальный или стеклянный конус',
    en: 'a small crystal or glass cone',
  },
  a_small_feather_or_piece_of_down: {
    ru: 'маленькое перо или пушинка',
    en: 'a small feather or piece of down',
  },
  a_small_square_of_silk: {
    ru: 'маленький шёлковый лоскут',
    en: 'a small square of silk',
  },
  a_snake_s_tongue: { ru: 'язык змеи', en: 'a snake’s tongue' },
  a_sprig_of_mistletoe: { ru: 'веточка омелы', en: 'a sprig of mistletoe' },
  a_statuette_of_the_target_worth_5_000_gp: {
    ru: 'статуэтка цели стоимостью от 5000 золотых',
    en: 'a statuette of the target worth 5,000+ GP',
  },
  a_statuette_of_yourself_worth_5_gp: {
    ru: 'ваша статуэтка стоимостью от 5 золотых',
    en: 'a statuette of yourself worth 5+ GP',
  },
  a_straight_piece_of_iron: {
    ru: 'прямой кусок железа',
    en: 'a straight piece of iron',
  },
  a_strip_of_white_cloth: { ru: 'полоска белой ткани', en: 'a strip of white cloth' },
  a_sumac_leaf: { ru: 'лист сумаха', en: 'a sumac leaf' },
  a_tart_and_a_feather: { ru: 'пирожное и перо', en: 'a tart and a feather' },
  a_tentacle: { ru: 'щупальце', en: 'a tentacle' },
  a_thin_sheet_of_lead: { ru: 'тонкий свинцовый лист', en: 'a thin sheet of lead' },
  a_weapon_with_which_you_have_proficiency_and_that_is_worth_1_cp: {
    ru: 'оружие, которым вы владеете, стоимостью от 1 медной монеты',
    en: 'a weapon with which you have proficiency and that is worth 1+ CP',
  },
  a_white_feather: { ru: 'белое перо', en: 'a white feather' },
  a_yew_leaf: { ru: 'лист тиса', en: 'a yew leaf' },
  an_agate_worth_1_000_gp_which_the_spell_consumes: {
    ru: 'агат стоимостью от 1000 золотых, расходуется заклинанием',
    en: 'an agate worth 1,000+ GP, which the spell consumes',
  },
  an_eggshell_and_a_glove: {
    ru: 'яичная скорлупа и перчатка',
    en: 'an eggshell and a glove',
  },
  an_eyelash_in_gum_arabic: {
    ru: 'ресница в гуммиарабике',
    en: 'an eyelash in gum arabic',
  },
  an_object_with_the_image_of_a_dragon_engraved_on_it_worth_500_gp: {
    ru: 'предмет с выгравированным изображением дракона стоимостью от 500 золотых',
    en: 'an object with the image of a dragon engraved on it worth 500+ GP',
  },
  ashes_from_burned_mistletoe: {
    ru: 'пепел сожжённой омелы',
    en: 'ashes from burned mistletoe',
  },
  bat_fur_and_a_piece_of_coal: {
    ru: 'шерсть летучей мыши и кусок угля',
    en: 'bat fur and a piece of coal',
  },
  burning_incense: { ru: 'горящие благовония', en: 'burning incense' },
  burning_incense_worth_10_gp_which_the_spell_consumes: {
    ru: 'горящие благовония стоимостью от 10 золотых, расходуются заклинанием',
    en: 'burning incense worth 10+ GP, which the spell consumes',
  },
  diamond_dust_worth_100_gp_which_the_spell_consumes: {
    ru: 'алмазная пыль стоимостью от 100 золотых, расходуется заклинанием',
    en: 'diamond dust worth 100+ GP, which the spell consumes',
  },
  diamonds_worth_25_000_gp_which_the_spell_consumes: {
    ru: 'алмазы стоимостью от 25000 золотых, расходуются заклинанием',
    en: 'diamonds worth 25,000+ GP, which the spell consumes',
  },
  for_each_of_the_spell_s_targets_one_jacinth_worth_1_000_gp_and_o: {
    ru: 'на каждую цель заклинания — один гиацинт стоимостью от 1000 золотых и один серебряный слиток стоимостью от 100 золотых, всё расходуется заклинанием',
    en: 'for each of the spell’s targets, one jacinth worth 1,000+ GP and one silver bar worth 100+ GP, all of which the spell consumes',
  },
  fur_from_a_bloodhound: { ru: 'шерсть ищейки', en: 'fur from a bloodhound' },
  fur_or_a_feather: { ru: 'мех или перо', en: 'fur or a feather' },
  gem_dust_worth_5_000_gp_which_the_spell_consumes: {
    ru: 'самоцветная пыль стоимостью от 5000 золотых, расходуется заклинанием',
    en: 'gem dust worth 5,000+ GP, which the spell consumes',
  },
  gold_dust_worth_25_gp_which_the_spell_consumes: {
    ru: 'золотая пыль стоимостью от 25 золотых, расходуется заклинанием',
    en: 'gold dust worth 25+ GP, which the spell consumes',
  },
  incense: { ru: 'благовония', en: 'incense' },
  incense_worth_1_000_gp_which_the_spell_consumes: {
    ru: 'благовония стоимостью от 1000 золотых, расходуются заклинанием',
    en: 'incense worth 1,000+ GP, which the spell consumes',
  },
  incense_worth_250_gp_which_the_spell_consumes_and_four_ivory_str: {
    ru: 'благовония стоимостью от 250 золотых, расходуются заклинанием, и четыре полоски слоновой кости стоимостью от 50 золотых каждая',
    en: 'incense worth 250+ GP, which the spell consumes, and four ivory strips worth 50+ GP each',
  },
  incense_worth_25_gp_which_the_spell_consumes: {
    ru: 'благовония стоимостью от 25 золотых, расходуются заклинанием',
    en: 'incense worth 25+ GP, which the spell consumes',
  },
  ink_worth_10_gp_which_the_spell_consumes: {
    ru: 'чернила стоимостью от 10 золотых, расходуются заклинанием',
    en: 'ink worth 10+ GP, which the spell consumes',
  },
  iron_filings: { ru: 'железные опилки', en: 'iron filings' },
  jade_dust_worth_10_gp_which_the_spell_consumes: {
    ru: 'нефритовая пыль стоимостью от 10 золотых, расходуется заклинанием',
    en: 'jade dust worth 10+ GP, which the spell consumes',
  },
  jade_dust_worth_25_gp: {
    ru: 'нефритовая пыль стоимостью от 25 золотых',
    en: 'jade dust worth 25+ GP',
  },
  mistletoe: { ru: 'омела', en: 'mistletoe' },
  mushroom_powder_worth_25_gp_which_the_spell_consumes: {
    ru: 'грибной порошок стоимостью от 25 золотых, расходуется заклинанием',
    en: 'mushroom powder worth 25+ GP, which the spell consumes',
  },
  one_150_gp_black_onyx_stone_for_each_corpse: {
    ru: 'один чёрный оникс стоимостью от 150 золотых за каждое тело',
    en: 'one 150+ GP black onyx stone for each corpse',
  },
  powdered_diamond_worth_1_000_gp_which_the_spell_consumes: {
    ru: 'алмазный порошок стоимостью от 1000 золотых, расходуется заклинанием',
    en: 'powdered diamond worth 1,000+ GP, which the spell consumes',
  },
  powdered_diamond_worth_200_gp_which_the_spell_consumes: {
    ru: 'алмазный порошок стоимостью от 200 золотых, расходуется заклинанием',
    en: 'powdered diamond worth 200+ GP, which the spell consumes',
  },
  powdered_rhubarb_leaf: {
    ru: 'порошок из листьев ревеня',
    en: 'powdered rhubarb leaf',
  },
  powdered_ruby_worth_1_500_gp_which_the_spell_consumes: {
    ru: 'рубиновый порошок стоимостью от 1500 золотых, расходуется заклинанием',
    en: 'powdered ruby worth 1,500+ GP, which the spell consumes',
  },
  powdered_silver_and_iron: {
    ru: 'порошок серебра и железа',
    en: 'powdered silver and iron',
  },
  rare_inks_worth_50_gp_which_the_spell_consumes: {
    ru: 'редкие чернила стоимостью от 50 золотых, расходуются заклинанием',
    en: 'rare inks worth 50+ GP, which the spell consumes',
  },
  rare_oils_worth_1_000_gp_which_the_spell_consumes: {
    ru: 'редкие масла стоимостью от 1000 золотых, расходуются заклинанием',
    en: 'rare oils worth 1,000+ GP, which the spell consumes',
  },
  ruby_dust_worth_1_000_gp: {
    ru: 'рубиновая пыль стоимостью от 1000 золотых',
    en: 'ruby dust worth 1,000+ GP',
  },
  ruby_dust_worth_1_500_gp_which_the_spell_consumes: {
    ru: 'рубиновая пыль стоимостью от 1500 золотых, расходуется заклинанием',
    en: 'ruby dust worth 1,500+ GP, which the spell consumes',
  },
  ruby_dust_worth_50_gp_which_the_spell_consumes: {
    ru: 'рубиновая пыль стоимостью от 50 золотых, расходуется заклинанием',
    en: 'ruby dust worth 50+ GP, which the spell consumes',
  },
  salt_and_powdered_silver_worth_100_gp_which_the_spell_consumes: {
    ru: 'соль и порошок серебра стоимостью от 100 золотых, расходуются заклинанием',
    en: 'salt and powdered silver worth 100+ GP, which the spell consumes',
  },
  seven_thorns: { ru: 'семь шипов', en: 'seven thorns' },
  soft_clay: { ru: 'мягкая глина', en: 'soft clay' },
  specially_marked_sticks_bones_cards_or_other_divinatory_tokens_w: {
    ru: 'особым образом помеченные палочки, кости, карты или иные гадательные знаки стоимостью от 25 золотых',
    en: 'specially marked sticks, bones, cards, or other divinatory tokens worth 25+ GP',
  },
  the_petrified_eye_of_a_newt: {
    ru: 'окаменевший глаз тритона',
    en: 'the petrified eye of a newt',
  },
  the_powder_of_a_crushed_black_pearl_worth_500_gp: {
    ru: 'порошок растёртой чёрной жемчужины стоимостью от 500 золотых',
    en: 'the powder of a crushed black pearl worth 500+ GP',
  },
  three_nut_shells: { ru: 'три ореховые скорлупки', en: 'three nut shells' },
  three_silver_pins: { ru: 'три серебряные булавки', en: 'three silver pins' },
  two_eggs: { ru: 'два яйца', en: 'two eggs' },
  two_lodestones: { ru: 'два магнетита', en: 'two lodestones' },
}

/**
 * Условие бонусного действия и реакции: хвост после запятой.
 *
 * Род в переводе не общий: «Бонусное действие, которое вы
 * совершаете…», но «Реакция, которую вы совершаете…». Поэтому хвост
 * переводится целиком вместе с «which you take», а не собирается
 * из кусков.
 */
export const SPELL_TIME_TAILS: Readonly<Record<string, SpellPhrase>> = {
  which_you_take_immediately_after_hitting_a_creature_with_a_melee: {
    ru: 'которое вы совершаете сразу после попадания по существу рукопашным оружием или безоружным ударом',
    en: 'which you take immediately after hitting a creature with a Melee weapon or an Unarmed Strike',
  },
  which_you_take_immediately_after_hitting_a_creature_with_a_weapo: {
    ru: 'которое вы совершаете сразу после попадания по существу оружием',
    en: 'which you take immediately after hitting a creature with a weapon',
  },
  which_you_take_immediately_after_hitting_a_target_with_a_melee_w: {
    ru: 'которое вы совершаете сразу после попадания по цели рукопашным оружием или безоружным ударом',
    en: 'which you take immediately after hitting a target with a Melee weapon or an Unarmed Strike',
  },
  which_you_take_in_response_to_taking_damage_from_a_creature_that: {
    ru: 'которую вы совершаете в ответ на урон от существа, которое вы видите в пределах 60 футов',
    en: 'which you take in response to taking damage from a creature that you can see within 60 feet of yourself',
  },
  which_you_take_when_you_are_hit_by_an_attack_roll_or_targeted_by: {
    ru: 'которую вы совершаете, когда по вам попадают броском атаки или нацеливают на вас «Волшебную стрелу»',
    en: 'which you take when you are hit by an attack roll or targeted by the Magic Missile spell',
  },
  which_you_take_when_you_or_a_creature_you_can_see_within_60_feet: {
    ru: 'которую вы совершаете, когда вы или существо, которое вы видите в пределах 60 футов, падаете',
    en: 'which you take when you or a creature you can see within 60 feet of you falls',
  },
  which_you_take_when_you_see_a_creature_within_60_feet_of_yoursel: {
    ru: 'которую вы совершаете, когда видите существо в пределах 60 футов, накладывающее заклинание с вербальным, соматическим или материальным компонентом',
    en: 'which you take when you see a creature within 60 feet of yourself casting a spell with Verbal, Somatic, or Material components',
  },
}

/**
 * Время накладывания, которое не разбирается грамматикой вовсе:
 * у «Роста растений» два разных времени с пояснениями в скобках.
 * Одна запись на весь SRD — правило ради неё не заводится.
 */
export const SPELL_TIMES: Readonly<Record<string, SpellPhrase>> = {
  action_overgrowth_or_8_hours_enrichment: {
    ru: 'Действие (буйный рост) или 8 часов (обогащение)',
    en: 'Action (Overgrowth) or 8 hours (Enrichment)',
  },
}

/** Слаг оборота: ключ словаря. Совпадает с тем, как собран файл. */
export function phraseKey(text: string): string {
  return text
    .toLowerCase()
    .replace(/\u2019/g, "'")
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64)
    .replace(/_+$/, '')
}

/** Обороты одного языка — то, что уходит в ресурсы i18next. */
export function spellPhrasesFor(language: 'ru' | 'en'): Record<string, string> {
  return Object.fromEntries(
    Object.entries({ ...SPELL_MATERIALS, ...SPELL_TIME_TAILS, ...SPELL_TIMES }).map(
      ([key, phrase]) => [key, phrase[language]],
    ),
  )
}
